import { useState } from "react";
import ReactJson from "react-json-view";
import SimpleTable from "../../../shared/SimpleTable";

const cardStyle = {
    backgroundColor: 'var(--white)',
    borderRadius: '.6rem',
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
    padding: '1rem 1.2rem',
    marginBottom: '1rem',
};

const sectionTitleStyle = {
    fontSize: '1rem',
    fontWeight: 600,
    paddingBottom: '.6rem',
    color: 'var(--darkerGrey)',
};

const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '.75rem',
};

const statBoxStyle = {
    padding: '.75rem',
    borderRadius: '.5rem',
    backgroundColor: 'var(--lightestGrey)',
};

const statLabelStyle = {
    fontSize: '.75rem',
    color: 'var(--grey)',
    textTransform: 'uppercase',
    letterSpacing: '.03em',
    marginBottom: '.25rem',
};

const statValueStyle = {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--darkerGrey)',
};

const fieldRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '.3rem 0',
    borderBottom: '1px solid var(--lightestGrey)',
    gap: '1rem',
};

const fieldLabelStyle = {
    color: 'var(--grey)',
    fontSize: '.9rem',
    minWidth: '120px',
    flexShrink: 0,
};

const fieldValueStyle = {
    fontSize: '.9rem',
    fontWeight: 500,
    color: 'var(--darkerGrey)',
    textAlign: 'right',
    wordBreak: 'break-word',
};

const parseEobClaimDetail = (record) => {
    const raw = record?.eobClaimDetail;
    if (!raw) return null;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }
    return raw;
};

const normalizeClaimLines = (claim) => {
    const lines = claim?.claimLine?.claimLine;
    if (!lines) return [];
    return Array.isArray(lines) ? lines : [lines];
};

const normalizeDiagnosisCodes = (diagnosisList) => {
    if (!diagnosisList) return [];

    const value = diagnosisList.string ?? diagnosisList.diagnosisCode ?? diagnosisList;

    if (Array.isArray(value)) {
        return value.filter((item) => item !== null && item !== undefined && item !== '').map(String);
    }

    if (typeof value === 'string' && value.trim()) {
        return [value.trim()];
    }

    return [];
};

const getClaimFormType = (claim) => {
    const type = String(claim?.type || '').toLowerCase();
    if (type === 'institutional') return 'institutional';
    if (type === 'professional') return 'professional';
    if (claim?.ub92Info) return 'institutional';
    if (claim?.hcfa1500Info) return 'professional';
    return 'unknown';
};

const getReviewTriggers = (reviewWrapper) => {
    if (!reviewWrapper) return [];
    const triggers = reviewWrapper.reviewTrigger;
    if (Array.isArray(triggers)) return triggers;
    if (triggers) return [triggers];
    return [];
};

const normalizeDiagnoses = (claim) => {
    const diagnoses = [];

    const principal = claim?.principalDiagnosis;
    if (principal) {
        const code = typeof principal === 'string' ? principal : principal?.diagnosisCode || principal?.code;
        if (code) diagnoses.push({ code: String(code), label: 'Principal' });
    }

    const reason = claim?.reasonForVisitDiagnosis?.diagnosisInfo;
    if (Array.isArray(reason)) {
        reason.forEach((item) => {
            if (item?.diagnosisCode) {
                diagnoses.push({ code: String(item.diagnosisCode), label: 'Reason for Visit' });
            }
        });
    } else if (reason?.diagnosisCode) {
        diagnoses.push({ code: String(reason.diagnosisCode), label: 'Reason for Visit' });
    }

    const other = claim?.otherDiagnosis?.diagnosisInfo;
    if (Array.isArray(other)) {
        other.forEach((item) => {
            if (item?.diagnosisCode) {
                diagnoses.push({ code: String(item.diagnosisCode), label: item.diagnosisTypeCode || 'Other' });
            }
        });
    } else if (other?.diagnosisCode) {
        diagnoses.push({ code: String(other.diagnosisCode), label: other.diagnosisTypeCode || 'Other' });
    }

    return diagnoses;
};

const getActivityExceptions = (exceptionWrapper) => {
    if (!exceptionWrapper) return [];
    const activity = exceptionWrapper.activityException;
    if (Array.isArray(activity)) return activity;
    if (activity) return [activity];
    return [];
};

const formatPersonName = (person) => {
    if (!person) return null;
    return [person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ');
};

const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const Badge = ({ children, color = 'var(--blue)', bg = 'var(--lightestBlue)' }) => (
    <span style={{
        display: 'inline-block',
        padding: '.2rem .8rem',
        borderRadius: '1rem',
        fontSize: '.8rem',
        fontWeight: 600,
        backgroundColor: bg,
        color,
        whiteSpace: 'nowrap',
    }}>
        {children}
    </span>
);

const Field = ({ label, value }) => {
    if (value === undefined || value === null || value === '') return null;
    return (
        <div style={fieldRowStyle}>
            <span style={fieldLabelStyle}>{label}</span>
            <span style={fieldValueStyle}>{String(value)}</span>
        </div>
    );
};

const Card = ({ title, children, defaultOpen = true, count }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={cardStyle}>
            <div
                style={{
                    ...sectionTitleStyle,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: open ? '.4rem' : 0,
                }}
                onClick={() => setOpen(!open)}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    {title}
                    {count !== undefined && (
                        <span style={{
                            fontSize: '.75rem',
                            color: 'var(--grey)',
                            backgroundColor: 'var(--lightestGrey)',
                            borderRadius: '1rem',
                            padding: '0 .5rem',
                            fontWeight: 500,
                        }}>
                            {count}
                        </span>
                    )}
                </span>
                <span style={{ fontSize: '.8rem', color: 'var(--grey)' }}>{open ? '▾' : '▸'}</span>
            </div>
            {open && children}
        </div>
    );
};

const Stat = ({ label, value }) => (
    <div style={statBoxStyle}>
        <div style={statLabelStyle}>{label}</div>
        <div style={statValueStyle}>{value || '—'}</div>
    </div>
);

const ExceptionList = ({ exceptions, title }) => {
    if (!exceptions.length) return null;
    return (
        <Card title={title} count={exceptions.length}>
            {exceptions.map((item, index) => {
                const message = item?.message;
                const description = message?.messageDescription || message?.messageCode;
                return (
                    <div key={index} style={{ padding: '.5rem 0', borderBottom: '1px solid var(--lightestGrey)' }}>
                        <p className="danger" style={{ fontWeight: 600, marginBottom: '.2rem' }}>{description}</p>
                        {message?.mappedMessage?.messageDescription && (
                            <p style={{ fontSize: '.85rem', color: 'var(--grey)' }}>
                                {message.mappedMessage.messageDescription}
                            </p>
                        )}
                        {message?.mappedRarMessage?.messageDescription && (
                            <p style={{ fontSize: '.85rem', color: 'var(--grey)' }}>
                                {message.mappedRarMessage.messageDescription}
                            </p>
                        )}
                    </div>
                );
            })}
        </Card>
    );
};

const ReviewTriggerList = ({ triggers, title }) => {
    if (!triggers.length) return null;
    return (
        <Card title={title} count={triggers.length}>
            {triggers.map((item, index) => {
                const message = item?.message;
                const description = message?.messageDescription || message?.messageCode;
                return (
                    <div key={index} style={{ padding: '.5rem 0', borderBottom: '1px solid var(--lightestGrey)' }}>
                        <p style={{ fontWeight: 600, marginBottom: '.2rem', color: 'var(--orange)' }}>{description}</p>
                    </div>
                );
            })}
        </Card>
    );
};

const PROFESSIONAL_LINE_COLUMNS = [
    { label: '#', attribute: 'claimLineId' },
    { label: 'CPT', attribute: 'serviceCode' },
    { label: 'Description', attribute: 'serviceCodeDesc' },
    { label: 'POS', attribute: 'placeOfService' },
    { label: 'Start', attribute: 'serviceStartDate' },
    { label: 'End', attribute: 'serviceEndDate' },
    { label: 'Requested', attribute: 'requestedAmount' },
    { label: 'Allowed', attribute: 'allowedAmount' },
    { label: 'Paid', attribute: 'paidAmount' },
    { label: 'Member', attribute: 'memberResponsibility' },
];

const INSTITUTIONAL_LINE_COLUMNS = [
    { label: '#', attribute: 'claimLineId' },
    { label: 'Rev', attribute: 'revenueCode' },
    { label: 'Revenue Desc', attribute: 'revenueCodeDesc' },
    { label: 'HCPCS', attribute: 'serviceCode' },
    { label: 'Description', attribute: 'serviceCodeDesc' },
    { label: 'Start', attribute: 'serviceStartDate' },
    { label: 'End', attribute: 'serviceEndDate' },
    { label: 'Requested', attribute: 'requestedAmount' },
    { label: 'Allowed', attribute: 'allowedAmount' },
    { label: 'Paid', attribute: 'paidAmount' },
    { label: 'Member', attribute: 'memberResponsibility' },
];

const formatProcessingDate = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value.slice(0, 10);
    return String(value);
};

const EobClaimDetailView = ({ record, hideIntro = false, hideRawJson = false }) => {
    const [showRawJson, setShowRawJson] = useState(false);
    const eob = parseEobClaimDetail(record);
    const claim = eob?.claim;

    if (!claim) {
        return (
            <div>
                <p className="sub-title">No eobClaimDetail found on this record.</p>
                <ReactJson
                    style={{ padding: '.5rem 0' }}
                    src={record}
                    name={false}
                    collapsed={1}
                    collapseStringsAfterLength={150}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    sortKeys
                />
            </div>
        );
    }

    const claimFormType = getClaimFormType(claim);
    const isInstitutional = claimFormType === 'institutional';
    const isProfessional = claimFormType === 'professional';

    const lines = normalizeClaimLines(claim);
    const diagnoses = normalizeDiagnoses(claim);
    const claimExceptions = getActivityExceptions(claim.claimException);
    const claimReviews = getReviewTriggers(claim.claimReviewTrigger);
    const patient = claim.patientInformation?.member;
    const subscriber = claim.subscriptionInformation?.subscriber;
    const benefitPlan = claim.subscriptionInformation?.benefitPlan;
    const payer = record.payerInformation;

    const hcfa = claim.hcfa1500Info;
    const ub92 = claim.ub92Info;
    const renderingFacility = hcfa?.renderingFacility;
    const professionalSupplier = claim.supplier || hcfa?.supplierInformation || ub92?.supplierInformation;
    const institutionalSupplier = claim.supplierLocation || ub92?.supplierInformation;
    const attending = claim.attendingInformation;

    const supplier = isInstitutional ? institutionalSupplier : professionalSupplier;

    const typeBadgeStyle = isInstitutional
        ? { color: 'var(--orange)', bg: 'var(--lightOragne)' }
        : isProfessional
            ? { color: 'var(--blue)', bg: 'var(--lightestBlue)' }
            : { color: 'var(--darkerGrey)', bg: 'var(--lightestGrey)' };

    const lineColumns = isInstitutional ? INSTITUTIONAL_LINE_COLUMNS : PROFESSIONAL_LINE_COLUMNS;
    const linesTitle = isInstitutional ? 'Service Lines (Revenue / HCPCS)' : 'Service Lines (CPT / POS)';

    const lineRows = lines.map((line) => {
        const benefitInfo = line.eobBaseBenefitInfo?.benefitInfo;
        return {
            ...line,
            diagnosisCodes: normalizeDiagnosisCodes(line.diagnosisList).join(', '),
            lineExceptions: getActivityExceptions(line.claimLineExceptions)
                .map((ex) => ex?.message?.messageDescription)
                .filter(Boolean)
                .join('; '),
            lineReviews: getReviewTriggers(line.claimLineReviewTriggers)
                .map((trigger) => trigger?.message?.messageDescription)
                .filter(Boolean)
                .join('; '),
            benefitDeductible: benefitInfo?.deductibleAmount,
            benefitCoinsurance: benefitInfo?.coInsuranceAmount,
            benefitCopay: benefitInfo?.coPayAmount,
        };
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {!hideIntro && (
            <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginBottom: '.6rem' }}>
                    <Badge color={typeBadgeStyle.color} bg={typeBadgeStyle.bg}>
                        {isInstitutional ? 'Institutional (UB-04)' : isProfessional ? 'Professional (CMS-1500)' : (claim.type || 'Claim')}
                    </Badge>
                    {record.claimStatus && (
                        <Badge
                            color={/denied|rejected|void/i.test(record.claimStatus) ? 'var(--red)' : /final|paid/i.test(record.claimStatus) ? 'var(--green)' : 'var(--orange)'}
                            bg={/denied|rejected|void/i.test(record.claimStatus) ? 'var(--lightRed)' : /final|paid/i.test(record.claimStatus) ? 'var(--lightGreen)' : 'var(--lightOragne)'}
                        >
                            {record.claimStatus}
                        </Badge>
                    )}
                    {!record.claimStatus && claim.state && <Badge color="var(--darkerGrey)" bg="var(--lightestGrey)">{claim.state}</Badge>}
                    {claim.inNetwork && (
                        <Badge
                            color={claim.inNetwork === 'Yes' ? 'var(--green)' : 'var(--orange)'}
                            bg={claim.inNetwork === 'Yes' ? 'var(--lightGreen)' : 'var(--lightOragne)'}
                        >
                            {claim.inNetwork === 'Yes' ? 'In Network' : 'Out of Network'}
                        </Badge>
                    )}
                    {claimExceptions.length > 0 && (
                        <Badge color="var(--red)" bg="var(--lightRed)">Has Exceptions</Badge>
                    )}
                    {claimReviews.length > 0 && (
                        <Badge color="var(--orange)" bg="var(--lightOragne)">Needs Review</Badge>
                    )}
                </div>

                <p className="title" style={{ marginBottom: '.2rem' }}>
                    {record.claimHccId || claim.hccId || claim.claimIdentifier}
                </p>
                <p className="sub-title">
                    Service {claim.serviceDateString || claim.startDate}
                    {claim.endDate && claim.endDate !== claim.startDate ? ` – ${claim.endDate}` : ''}
                </p>
                {record.sk && <p className="sub-title" style={{ wordBreak: 'break-all' }}>{record.sk}</p>}
            </div>
            )}

            <Card title="Amounts">
                <div style={gridStyle}>
                    <Stat label="Coverage" value={claim.coverageAmount || claim.requestedFeeAmount} />
                    <Stat label="Allowed" value={claim.allowedAmount} />
                    <Stat label="Paid (HCC)" value={claim.hccAmount} />
                    <Stat label="Member Paid" value={claim.memberPaidAmount || claim.memberAmount} />
                </div>
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                <Card title="Patient">
                    <Field label="Name" value={formatPersonName(patient)} />
                    <Field label="HCC ID" value={claim.patientInformation?.hccId} />
                    <Field label="Relationship" value={claim.patientInformation?.relationshipToSubscriber} />
                    <Field label="DOB" value={patient?.birthDate} />
                    <Field label="Gender" value={patient?.gender || patient?.genderCode} />
                </Card>

                <Card title="Subscriber">
                    <Field label="Name" value={formatPersonName(subscriber)} />
                    <Field label="HCC ID" value={claim.subscriptionInformation?.hccId} />
                    <Field label="Group" value={claim.subscriptionInformation?.groupNumber} />
                    <Field label="Plan" value={benefitPlan?.name} />
                    <Field label="Plan HCC ID" value={benefitPlan?.hccId} />
                </Card>

                <Card title={isInstitutional ? 'Facility / Supplier' : 'Provider'}>
                    <Field label="Name" value={supplier?.name || supplier?.supplierBillingName || supplier?.supplierName} />
                    <Field label="NPI" value={supplier?.NPI || supplier?.npi} />
                    <Field label="TIN" value={supplier?.taxIdentificationNumber} />
                    {isInstitutional && <Field label="Location HCC ID" value={supplier?.hccId} />}
                    <Field label="Account" value={claim.subscriptionInformation?.account?.name} />
                    <Field label="Employer" value={claim.subscriptionInformation?.topAccount?.name} />
                </Card>

                {isProfessional && renderingFacility && (
                    <Card title="Rendering Facility">
                        <Field label="Facility" value={renderingFacility.facilityName} />
                        <Field label="NPI" value={renderingFacility.npi} />
                        <Field label="Address" value={[
                            renderingFacility.address?.address,
                            renderingFacility.address?.city,
                            renderingFacility.address?.stateCode,
                            renderingFacility.address?.zipCode,
                        ].filter(Boolean).join(', ')} />
                    </Card>
                )}

                {isInstitutional && attending && (
                    <Card title="Attending Physician">
                        <Field label="Name" value={formatPersonName(attending.practitioner)} />
                        <Field label="NPI" value={attending.NPI} />
                        <Field label="HCC ID" value={attending.hccId} />
                        <Field label="Specialty" value={attending.primarySpecialty?.taxonomyName} />
                    </Card>
                )}

                <Card title="Claim Info">
                    <Field label="External Claim ID" value={claim.externalClaimId} />
                    <Field label="Clearinghouse Trace" value={claim.clearingHouseTraceNumber} />
                    <Field label="Receipt Date" value={claim.receiptDate} />
                    <Field label="Processing Date" value={formatProcessingDate(claim.processingDate)} />
                    {isInstitutional && <Field label="Type of Bill" value={claim.typeOfBill || claim.typeOfBillCode} />}
                    {isInstitutional && <Field label="Medical Record #" value={claim.medicalRecordNumber} />}
                    {isInstitutional && <Field label="Admission Source" value={claim.admissionSource} />}
                    {isInstitutional && <Field label="Admission Type" value={claim.admissionTypeCode} />}
                    {isInstitutional && <Field label="Discharge Status" value={claim.dischargeStatus} />}
                    {isProfessional && <Field label="Admission Type" value={claim.admissionTypeCode} />}
                    <Field label="Payment Number" value={record.paymentNumber} />
                    <Field label="Version" value={record.version} />
                </Card>
            </div>

            {isInstitutional && (
                <Card title="Institutional Details" defaultOpen={false}>
                    <Field label="Statement Period" value={[claim.startDate, claim.endDate].filter(Boolean).join(' – ')} />
                    <Field label="Patient Control #" value={claim.patientControlNumber} />
                    <Field label="Patient Account #" value={claim.patientAccountNumber} />
                    <Field label="Frequency" value={claim.frequency} />
                    <Field label="Benefit Assignment" value={claim.benefitAssignment} />
                </Card>
            )}

            {isProfessional && hcfa && (
                <Card title="CMS-1500 Details" defaultOpen={false}>
                    <Field label="Subscriber" value={formatPersonName(hcfa.subscriberInformation?.individual)} />
                    <Field label="Member" value={formatPersonName(hcfa.memberInformation?.individual)} />
                    <Field label="Assignment Accepted" value={hcfa.supplierInformation?.assignmentAcceptance} />
                </Card>
            )}

            {diagnoses.length > 0 && (
                <Card title="Diagnoses" count={diagnoses.length}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                        {diagnoses.map((dx) => (
                            <Badge key={`${dx.label}-${dx.code}`} color="var(--darkerGrey)" bg="var(--lightestGrey)">
                                {dx.code} ({dx.label})
                            </Badge>
                        ))}
                    </div>
                </Card>
            )}

            <ExceptionList exceptions={claimExceptions} title="Claim Exceptions" />
            <ReviewTriggerList triggers={claimReviews} title="Claim Review Triggers" />

            <Card title={linesTitle} count={lines.length}>
                {lines.length > 0 ? (
                    <>
                        <SimpleTable columns={lineColumns} disableHeaderFormatting rows={lineRows} />
                        {lineRows.map((line, index) => (
                            <div key={line.claimLineId || index} style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--lightestGrey)' }}>
                                <p style={{ fontWeight: 600, marginBottom: '.4rem' }}>
                                    Line {line.claimLineId || index + 1}:{' '}
                                    {isInstitutional
                                        ? `${line.revenueCode || '—'} / ${line.serviceCode || '—'}`
                                        : line.serviceCode}
                                    {' — '}{line.serviceCodeDesc || line.revenueCodeDesc}
                                </p>
                                <div style={gridStyle}>
                                    <Stat label="Units" value={line.units} />
                                    {isProfessional && <Stat label="POS" value={line.placeOfService} />}
                                    {isInstitutional && <Stat label="Revenue" value={line.revenueCode} />}
                                    <Stat label="Requested" value={line.requestedAmount} />
                                    <Stat label="Allowed" value={line.allowedAmount} />
                                    <Stat label="Paid" value={line.paidAmount} />
                                    <Stat label="Member Resp" value={line.memberResponsibility} />
                                </div>
                                {isInstitutional && (line.benefitDeductible || line.benefitCoinsurance || line.benefitCopay) && (
                                    <div style={{ ...gridStyle, marginTop: '.5rem' }}>
                                        <Stat label="Deductible" value={line.benefitDeductible} />
                                        <Stat label="Coinsurance" value={line.benefitCoinsurance} />
                                        <Stat label="Copay" value={line.benefitCopay} />
                                    </div>
                                )}
                                {line.lineExceptions && (
                                    <p className="danger" style={{ fontSize: '.9rem', marginTop: '.5rem' }}>
                                        {line.lineExceptions}
                                    </p>
                                )}
                                {line.lineReviews && (
                                    <p style={{ fontSize: '.9rem', marginTop: '.5rem', color: 'var(--orange)' }}>
                                        Review: {line.lineReviews}
                                    </p>
                                )}
                                {line.diagnosisCodes && (
                                    <p style={{ fontSize: '.85rem', color: 'var(--grey)', marginTop: '.3rem' }}>
                                        Dx: {line.diagnosisCodes}
                                    </p>
                                )}
                            </div>
                        ))}
                    </>
                ) : (
                    <p className="sub-title">No service lines on this claim.</p>
                )}
            </Card>

            {payer && (
                <Card title="Payer" defaultOpen={false}>
                    <Field label="Name" value={payer.hicName} />
                    <Field label="Tax ID" value={payer.taxId} />
                    <Field label="Address" value={[
                        payer.hicAddress?.address,
                        payer.hicAddress?.city,
                        payer.hicAddress?.stateCode,
                        payer.hicAddress?.zipCode,
                    ].filter(Boolean).join(', ')} />
                </Card>
            )}

            {(record.pk || record.externalId || record.createdAt) && (
                <Card title="Record Metadata" defaultOpen={false}>
                    <Field label="Member UUID (pk)" value={record.pk} />
                    <Field label="External ID" value={record.externalId} />
                    <Field label="Created" value={formatDate(record.createdAt)} />
                    <Field label="Updated" value={formatDate(record.updatedAt)} />
                </Card>
            )}

            {!hideRawJson && (
            <button
                type="button"
                onClick={() => setShowRawJson(!showRawJson)}
                className="clickable-link"
                style={{ alignSelf: 'flex-start', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
            >
                {showRawJson ? 'Hide Raw JSON' : 'Show Raw JSON'}
            </button>
            )}

            {!hideRawJson && showRawJson && (
                <Card title="eobClaimDetail" defaultOpen>
                    <ReactJson
                        style={{ padding: '.5rem 0' }}
                        src={eob}
                        name={false}
                        collapsed={2}
                        collapseStringsAfterLength={150}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        sortKeys
                    />
                </Card>
            )}
        </div>
    );
};

export default EobClaimDetailView;
